<template>
  <div class="spotlight-page bc-brand mh-101vh">
    <!-- 404 -->
    <div v-if="!article" class="spotlight-not-found d-f ai-c jc-c fd-c p-xl">
      <p class="mute">Article not found.</p>
      <router-link to="/" class="mute font-small m-t-sm text-link">← Back to Algorhythm</router-link>
    </div>

    <!-- Article -->
    <article v-else>
      <!-- Intro block -->
      <div class="spotlight-intro-block bc-black ta-c p-t-nav">

        <div class="intro-v-divider"></div>

        <div class="intro-name-section">
          <p class="intro-series-label mute font-small2 tt-up">Spotlight</p>
          <h1 class="spotlight-headline">{{ article.headline }}</h1>
          <div class="spotlight-divider"></div>
          <p v-if="titlesString" class="intro-titles tt-up mute font-small2 m-t-lg">{{ titlesString }}</p>
        </div>

        <div class="intro-v-divider"></div>

        <div class="intro-text-section">
          <p class="spotlight-intro-text">{{ article.intro }}</p>
        </div>

        <div class="intro-meta tt-up font-small2">
          <p class="mute">Interview by Niklaz Lönnqvist</p>
          <p class="mute">{{ formattedDate }}</p>
        </div>

      </div>

      <!-- Fullscreen hero -->
      <SpotlightHeader :article="article" />

      <!-- Q&A in editorial column -->
      <div class="editorial-col p-t-xl m-b-2xl">
        <SpotlightQA
          :qa="article.qa || []"
          :inline-images="article.inlineImages || []"
          :artist-name="article.artistName"
        />

        <div v-if="article.featuredTrackUrl" class="m-t-xl b-top p-t-xl">
          <SpotlightTrackEmbed :track-url="article.featuredTrackUrl" />
        </div>
      </div>

      <!-- Profile — full width -->
      <Profile
        v-if="profileData"
        :profile-data="profileData"
        :uid="profileUid"
        :loading="false"
        :is-own-profile="false"
        @openUsersList="openUsersList"
      />

      <!-- Footer — full width -->
      <SpotlightFooter :current-slug="article.slug" />
    </article>

    <UsersListSidebar
      :open="!!usersListOpenFor"
      :title="usersListOpenFor"
      :users-list="usersList"
      @close="usersListOpenFor = ''"
    />
  </div>
</template>

<script>
import articles from './articles.json'
import { rtdb } from '@/db.js'
import SpotlightHeader from './SpotlightHeader.vue'
import SpotlightQA from './SpotlightQA.vue'
import SpotlightTrackEmbed from './SpotlightTrackEmbed.vue'
import SpotlightFooter from './SpotlightFooter.vue'
import Profile from '../profile/Profile2.vue'
import UsersListSidebar from '@/components/profile/UsersListSidebar.vue'

export default {
  name: 'SpotlightArticle',
  components: {
    SpotlightHeader,
    SpotlightQA,
    SpotlightTrackEmbed,
    SpotlightFooter,
    Profile,
    UsersListSidebar,
  },
  data() {
    return {
      article: null,
      profileData: null,
      profileUid: null,
      usersList: [],
      usersListOpenFor: '',
    }
  },
  metaInfo() {
    if (!this.article) return { title: 'Spotlight' }
    return {
      title: `${this.article.artistName} — Algorhythm Spotlights`,
    }
  },
  computed: {
    formattedDate() {
      if (!this.article || !this.article.publishedAt) return ''
      const d = new Date(this.article.publishedAt + 'T00:00:00')
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },
    titlesString() {
      if (!this.article || !this.article.genres || !this.article.genres.length) return ''
      return this.article.genres.map(g => g.charAt(0).toUpperCase() + g.slice(1)).join(' / ')
    },
  },
  created() {
    const slug = this.$route.params.slug
    this.article = articles[slug] || null
    if (this.article?.profileHandle) {
      this.fetchProfile(this.article.profileHandle)
    }
  },
  methods: {
    openUsersList(payload) {
      this.usersList = payload.followers || payload.followings
      this.usersListOpenFor = Object.keys(payload)[0]
      ;(this.usersList ?? []).forEach((uid) => {
        this.$store.dispatch('cacheUserData', { uid })
      })
    },
    fetchProfile(handle) {
      rtdb.ref('userSettings')
        .orderByChild('usernameUrl')
        .equalTo(handle)
        .once('value', (snap) => {
          if (snap.hasChildren()) {
            const [uid, userData] = Object.entries(snap.val())[0]
            this.profileUid = uid
            this.profileData = userData
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";

.spotlight-not-found {
  min-height: 100vh;
}

/* Intro block */
.spotlight-intro-block {
  width: 100%;
  box-sizing: border-box;
  padding-bottom: $mg-desktop-2xl;
}

.intro-v-divider {
  width: 1px;
  height: 80px;
  background-color: $line-color;
  margin: $mg-desktop-xl auto;

  @media #{$mobile} {
    height: 48px;
    margin: $mg-desktop-lg auto;
  }
}

.intro-meta {
  padding-top: $mg-desktop-xl;
}

.intro-name-section {
}

.intro-series-label {
  letter-spacing: 0.15em;
  margin-bottom: $mg-desktop-lg;
}

.intro-text-section {
  // padding-bottom: $mg-desktop-2xl;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media #{$mobile} {
    padding-left: $mg-desktop;
    padding-right: $mg-desktop;
  }
}

.intro-titles {
  letter-spacing: 0.12em;
}

.spotlight-headline {
  font-size: clamp(3.5rem, 10vw, 7rem);
  line-height: 1;
  color: $text-color;
  margin: 0;
}

.spotlight-divider {
  width: 40px;
  height: 1px;
  background-color: $text-color;
  margin: $mg-desktop-lg auto 0;
}

.spotlight-intro-text {
  font-size: $h2-d;
  color: $text-color;
  margin: 0;

  @media #{$mobile} {
    font-size: $h2-m;
  }
}

::v-deep #profile .bg-containter {
  background-color: transparent !important;
  border-bottom: 1px solid $line-color !important;
}

::v-deep #profile .ticker {
  display: none !important;
}
</style>
